/**
 * Canvas 8 side-navigation icons.
 *
 * `Layout.SideNav` items accept `icon: IconProps['svg']` — that is, the raw SVG
 * component from `@design-systems/react-icons`, NOT a rendered React element.
 *
 * Icons are keyed by the nav-item label constant so the transform in
 * Sidenavigation.tsx can look them up without touching SideNavigationItems.js.
 */
import {
  BarcodeIcon,
  CartIcon,
  CatalogIcon,
  ChartBarVerticalIcon,
  FillBackstockIcon,
  FoldersIcon,
  GearIcon,
  HouseIcon,
  InboxIcon,
  LocationMapIcon,
  PencilIcon,
  PersonGroupIcon,
  StoreIcon,
} from '@design-systems/react-icons'
import type { IconProps } from '@design-systems/react-icons'
import {
  HOME_LABEL,
  INTAKE_HDR_LABEL,
  CONCEPT_HDR_LABEL,
  CHANGE_HDR_LABEL,
  PROGRAM_LABEL,
  TEAMS_LABEL,
  LOCATIONS_SEARCH_LABEL,
  ISC_PARENT_LABEL,
  CATALOG_PARENT_LABEL,
  SPATIO_HDR_LABEL,
  ADMIN,
} from '../../../MicroFrontEnds/utility/constants'

export type SvgIcon = IconProps['svg']

/**
 * Map from a primary nav item label to its Canvas 8 icon component.
 * Only top-level (primary) items have icons — secondary items do not.
 */
export const NAV_ICON_MAP: Record<string, SvgIcon> = {
  [HOME_LABEL]: HouseIcon,

  // Intake — submissions / requests inbox
  [INTAKE_HDR_LABEL]: InboxIcon,

  // Design Initiative — design / drafting
  [CONCEPT_HDR_LABEL]: PencilIcon,

  // Change
  [CHANGE_HDR_LABEL]: FillBackstockIcon,

  // Programs — a program groups multiple projects/folders
  [PROGRAM_LABEL]: FoldersIcon,

  // Teams
  [TEAMS_LABEL]: PersonGroupIcon,

  // Locations
  [LOCATIONS_SEARCH_LABEL]: LocationMapIcon,

  // Indirect Service Catalog — it is literally a catalog of services
  [ISC_PARENT_LABEL]: CatalogIcon,

  // Procurement Services — purchasing / cart
  [CATALOG_PARENT_LABEL]: CartIcon,

  // Item & Catalog Operations — TCINs are identified by barcodes
  'Item & Catalog Operations': BarcodeIcon,

  // Reports
  Reports: ChartBarVerticalIcon,

  // Corporate Real Estate — physical store / building
  [SPATIO_HDR_LABEL]: StoreIcon,

  // Admin
  [ADMIN]: GearIcon,
}
